import {
  $,
  component$,
  noSerialize,
  useOnWindow,
  useSignal,
  useStore,
  useStyles$,
  type QRL,
} from "@builder.io/qwik";
import { OFFSET_TOP } from "./constants";
import AdClose from "~/components/ui/AdClose";
import styles from "./style.css?inline";
import type { Measures } from "./types";
import { publicEnv } from "~/env/publicEnv";

const AdvGptBoxContainer1 = component$(() => {
  useStyles$(styles);
  const containerRef = useSignal<HTMLDivElement>(); // keeps the height according to content
  const contentRef = useSignal<HTMLDivElement>(); // alternates between static or fixed
  const closeContainerRef = useSignal<HTMLDivElement>(); // close button positioning
  const isFloating = useSignal(false);
  const floatingClosePosition = useSignal(0);
  const measures = useStore<Measures>({ left: 0, width: 0, right: 0 });
  const intersectionObserver = useSignal<IntersectionObserver>();

  const handleClosePosition = $(() => {
    if (!contentRef.value) return;
    const advRawContainer = contentRef.value.querySelector(
      "#adv-gpt-box-container1",
    );
    if (!advRawContainer) return;
    let advContent: HTMLElement | null =
      advRawContainer.querySelector("iframe");
    if (!advContent) advContent = advRawContainer.querySelector("div");
    if (!advContent) return;
    const contentRect = contentRef.value.getBoundingClientRect();
    const advRect = advContent.getBoundingClientRect();
    const advLeftOffset = advRect.left - contentRect.left;
    const closeWidth =
      closeContainerRef.value?.getBoundingClientRect().width ?? 0;
    floatingClosePosition.value = advRect.width - closeWidth + advLeftOffset;
  });

  const handleStickyPositioning: QRL<
    (isIntersecting: boolean, containerY: number) => void
  > = $((isIntersecting, containerY) => {
    if (!containerRef.value || !contentRef.value) return;
    const shouldFloat = !isIntersecting && containerY < OFFSET_TOP;

    if (shouldFloat) {
      containerRef.value.style.zIndex = "30"; //Same that is applied by above class
      const advRawContainer = contentRef.value.querySelector(
        "#adv-gpt-box-container1",
      );
      if (!advRawContainer) return;
      // no adv
      if (advRawContainer.childNodes.length === 0) return;
      isFloating.value = true;
      handleClosePosition();
    } else {
      containerRef.value.style.removeProperty("z-index");
      isFloating.value = false;
    }
  });

  // We observe container and decide if content must become fixed or stay static
  const handleIntersection: QRL<IntersectionObserverCallback> = $((entries) => {
    for (const containerEntry of entries) {
      if (!contentRef.value || !containerRef.value) return;

      const containerY = containerEntry.boundingClientRect.y;
      handleStickyPositioning(containerEntry.isIntersecting, containerY);
    }
  });

  // We observe changes in content size so that container can keep its height
  const handleContentResize: QRL<ResizeObserverCallback> = $((entries) => {
    if (!containerRef.value) return;

    for (const contentEntry of entries) {
      const { height } = contentEntry.contentRect;
      if (height > 0) {
        containerRef.value.style.minHeight = `${height}px`;
      }
    }
  });

  const handleWindowResize = $(() => {
    if (contentRef.value && containerRef.value) {
      const { x, width } = containerRef.value.getBoundingClientRect();
      measures.left = x;
      measures.right = x + width;
      measures.width = width;
    }
    if (isFloating.value) handleClosePosition();
  });

  useOnWindow(
    "DOMContentLoaded",
    $(() => {
      if (!contentRef.value || !containerRef.value) return;
      handleWindowResize();
      const containerY = containerRef.value.getBoundingClientRect().y;
      const isIntersecting = containerY > OFFSET_TOP;
      handleStickyPositioning(isIntersecting, containerY);

      // Content alternates between position: fixed/static
      intersectionObserver.value = noSerialize(
        new IntersectionObserver(handleIntersection, {
          threshold: 1,
          rootMargin: `-${OFFSET_TOP}px 0px 0px 0px`,
        }),
      );

      intersectionObserver.value?.observe(containerRef.value);

      // Container must keep the height
      const resizeObserver = new ResizeObserver(handleContentResize);

      resizeObserver.observe(contentRef.value);
    }),
  );

  useOnWindow("resize", handleWindowResize);

  const handleClose = $(() => {
    isFloating.value = false;
    intersectionObserver.value?.disconnect();
  });

  return (
    <div
      ref={containerRef}
      class={["sgtv-w-full", { "sgtv-invisible": isFloating.value }]}
    >
      <div
        ref={contentRef}
        class="sgtv-visible sgtv-bg-transparent sgtv-contain-layout sgtv-perspective-[1px]"
        style={{
          top: `${OFFSET_TOP}px`,
          position: isFloating.value ? "fixed" : "static",
          left: `${measures.left}px`,
          right: `${measures.right}px`,
          width: `${measures.width}px`,
        }}
      >
        <div
          ref={closeContainerRef}
          class={[
            "sgtv-absolute -sgtv-top-[26px] sgtv-h-[26px] sgtv-w-max sgtv-overflow-hidden",
            { "sgtv-h-0 sgtv-opacity-0": !isFloating.value },
          ]}
          style={{
            left: `${floatingClosePosition.value}px`,
          }}
        >
          <AdClose onClick={handleClose} />
        </div>
        {publicEnv.ENABLE_ADV_UI_PLACEHOLDER ? (
          <div class="sgtv-aspect-square sgtv-bg-accent-500">
            Ad placeholder
          </div>
        ) : (
          <div class="sgtv-ml-auto sgtv-mr-auto sgtv-table">
            <div id="adv-gpt-box-container1" />
          </div>
        )}
      </div>
    </div>
  );
});

export default AdvGptBoxContainer1;
